import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Link2, Unplug, AlertTriangle } from "lucide-react";
import { api } from "@/lib/auth";

export default function WorkspaceCard() {
  const [state, setState] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/oauth/google/status").then((r) => setState(r.data)).catch(() => setState({ configured: false, connected: false }));

  useEffect(() => {
    load();
    if (params.get("google") === "connected") {
      toast.success("Google Workspace connected");
      params.delete("google");
      setParams(params, { replace: true });
    } else if (params.get("google") === "error") {
      toast.error("Google connection failed. Please check the redirect URI in Google Cloud Console and try again.");
      params.delete("google");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    try {
      const { data } = await api.get("/oauth/google/login");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Google connection");
    }
  };

  const disconnect = async () => {
    await api.post("/oauth/google/disconnect");
    toast.success("Disconnected");
    load();
  };

  return (
    <section className="gold-frame rounded-2xl bg-obsidian-800 p-6 sm:p-8" data-testid="workspace-card">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold">Google Workspace</p>
      <h2 className="mt-3 font-display text-2xl font-medium text-ivory">Gmail & Calendar sync</h2>
      <p className="mt-3 text-sm leading-relaxed text-ivory/60">
        When connected, every enquiry is emailed to your Workspace inbox and
        added to your Google Calendar automatically.
      </p>

      {state === null ? (
        <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/40">Checking…</p>
      ) : !state.configured ? (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-gold/20 bg-obsidian-900 p-4" data-testid="workspace-not-configured">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className="text-xs leading-relaxed text-ivory/65">
            Waiting for Google Cloud credentials (Client ID & Secret). Once added, a Connect button appears here.
          </p>
        </div>
      ) : state.connected ? (
        <div className="mt-6 space-y-4" data-testid="workspace-connected">
          <p className="inline-flex items-center gap-2 text-sm text-ivory">
            <CheckCircle2 className="h-4 w-4 text-gold" /> Connected as{" "}
            <span className="text-gold">{state.account?.email || "Workspace account"}</span>
          </p>
          <button onClick={disconnect} data-testid="workspace-disconnect-button" className="inline-flex items-center gap-2 rounded-full border border-gold/30 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:bg-gold hover:text-obsidian-900">
            <Unplug className="h-3.5 w-3.5" /> Disconnect
          </button>
        </div>
      ) : (
        <button onClick={connect} data-testid="workspace-connect-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-obsidian-900 hover:shadow-[0_0_32px_rgba(217,164,65,0.35)]">
          <Link2 className="h-4 w-4" /> Connect Google Workspace
        </button>
      )}
    </section>
  );
}
